import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import FileUploadSection from '../components/FileUploadSection';
import FileList from '../components/FileList';
import ActionButtons from '../components/ActionButtons';

const ToolPage = ({
  onDrop,
  getRootProps,
  getInputProps,
  isDragActive,
  fileInputRef,
  handleFileSelect,
  loading,
  files,
  thumbnails,
  loadingThumbnails,
  removeFile,
  handleOnDragEnd,
  pageCounts,
  splitRanges,
  setSplitRanges,
  compressionQuality,
  setCompressionQuality,
  rotationAngle,
  setRotationAngle,
  password,
  setPassword,
  watermarkText,
  setWatermarkText,
  watermarkOpacity,
  setWatermarkOpacity,
  formFields,
  formData,
  setFormData,
  handleFillForm,
  handleConvertToWord,
  handlers,
  mergeMessage,
  mergeMessageType,
  resultUrl,
  resultFilename, // Add this prop
  splitResult, 
  convertedImagesResult,
  handleClear,
  handleGoHome,
}) => {
  const { t } = useTranslation();
  const { tool } = useParams();

  const handleDownload = async () => {
    try {
      const response = await fetch(resultUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resultFilename || 'download.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  console.log('ToolPage rendered');
  console.log('tool', tool);
  console.log('mergeMessage', mergeMessage);
  console.log('mergeMessageType', mergeMessageType);
  console.log('resultUrl', resultUrl);
  console.log('splitResult', splitResult);
  console.log('convertedImagesResult', convertedImagesResult);

  return (
    <div className="tool-page">
      <div className="section-description">
        <p>{t(`app.desc${tool.charAt(0).toUpperCase() + tool.slice(1)}`)}</p>
      </div>
      <FileUploadSection
        onDrop={onDrop}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        fileInputRef={fileInputRef}
        handleFileSelect={handleFileSelect}
        loading={loading}
      />
      {files.length > 0 && (
        <FileList
          key={files[0]?.id} // Add key to force re-render when file changes
          files={files}
          thumbnails={thumbnails}
          loadingThumbnails={loadingThumbnails}
          removeFile={removeFile}
          handleOnDragEnd={handleOnDragEnd}
        />
      )}
      
      {tool === 'compress' && (
        <div className="tool-options-section">
          <label htmlFor="compressionQuality">{t('labels.compressionQuality')}:</label>
          <input
            type="range"
            id="compressionQuality"
            min="0"
            max="100"
            value={compressionQuality}
            onChange={(e) => setCompressionQuality(e.target.value)}
          />
          <span>{compressionQuality}%</span>
        </div>
      )}

      {tool === 'split' && files.length === 1 && (
        <div className="tool-options-section">
          <p>{t('app.splitInstructions')}</p>
          <p>{t('messages.totalPages', { count: pageCounts[files[0].id] })}</p>
          <input
            type="text"
            placeholder={t('app.splitPlaceholder')}
            value={splitRanges}
            onChange={(e) => setSplitRanges(e.target.value)}
          />
        </div>
      )}

      {tool === 'rotate' && files.length === 1 && (
        <div className="tool-options-section">
          <label htmlFor="rotationAngle">{t('labels.rotationAngle')}:</label>
          <select
            id="rotationAngle"
            value={rotationAngle}
            onChange={(e) => setRotationAngle(e.target.value)}
          >
            <option value="90">90°</option>
            <option value="180">180°</option>
            <option value="270">270°</option>
          </select>
        </div>
      )}

      {tool === 'watermark' && files.length === 1 && (
        <div className="tool-options-section">
          <input
            type="text"
            placeholder={t('placeholders.watermark')}
            value={watermarkText}
            onChange={(e) => setWatermarkText(e.target.value)}
          />
          <label htmlFor="watermarkOpacity">{t('labels.opacity')}:</label>
          <input
            type="range"
            id="watermarkOpacity"
            min="0"
            max="1"
            step="0.1"
            value={watermarkOpacity}
            onChange={(e) => setWatermarkOpacity(e.target.value)}
          />
          <span>{Math.round(watermarkOpacity * 100)}%</span>
        </div>
      )}
      
      {/* Always render the alert container if there's a message or resultUrl */}
      {(mergeMessage || resultUrl || (splitResult && splitResult.length > 0)) && (
        <div className={`alert alert-${mergeMessageType}`}>
          <span>{mergeMessage}</span>
          {mergeMessageType === 'success' && resultUrl && (
            <button onClick={handleDownload} className="download-btn">
              {t('buttons.download')}
            </button>
          )}
          {mergeMessageType === 'success' && splitResult && splitResult.length > 0 && (
            <div className="split-results">
              {splitResult.map((file, index) => (
                <a key={index} href={`${file.url}`} download={file.filename} className="download-btn" target="_blank" rel="noopener noreferrer">
                  {t('buttons.download')} {file.filename}
                </a>
              ))}
            </div>
          )}
          {mergeMessageType === 'success' && convertedImagesResult && convertedImagesResult.length > 0 && (
            <div className="image-results">
              {convertedImagesResult.map((image, index) => (
                <div key={index} className="image-result-item">
                  <img src={`${image.url}`} alt={`page-${image.page}`} />
                  <a href={`${image.url}`} download={image.filename} className="download-btn" target="_blank" rel="noopener noreferrer">
                    {t('buttons.download')} {image.filename}
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <ActionButtons activeTab={tool} handlers={handlers} disabled={loading} />
      {files.length > 0 && (
        <div className="clear-button-container">
          <button onClick={handleClear} className="clear-btn">
            {t('buttons.clear')}
          </button>
        </div>
      )}
      <div className="home-button-container">
        <button onClick={handleGoHome} className="home-btn">
          {t('buttons.home')}
        </button>
      </div>
    </div>
  );
};

export default ToolPage;